import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { FaSearch, FaEye, FaFilter, FaArrowLeft, FaTruckLoading, FaListUl, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";

function Orders() {
  const location = useLocation();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [allSchools, setAllSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  // Status Categories
  const ACTIVE_STATUS = ["Pending", "Processing", "On the Way"];
  const SHIPPED_STATUS = ["Shipped", "Delivered"];
  const CANCEL_STATUS = ["Cancelled", "Return Requested"];

  // Dropdown Options
  const STATUS_OPTIONS = ["Pending", "Processing", "On the Way", "Shipped", "Delivered", "Cancelled"];

  const [viewMode, setViewMode] = useState("all");
  const [searchName, setSearchName] = useState("");
  const [searchSchool, setSearchSchool] = useState(location.state?.filterSchool || "");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, schoolsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/orders/admin/orders`),
        axios.get(`${API_BASE_URL}/schools`)
      ]);
      setOrders(ordersRes.data);
      setAllSchools(schoolsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ UPDATE ORDER STATUS MANUALLY
  // ✅ UPDATE ORDER STATUS (Improved)
const updateOrderStatus = async (id, newStatus) => {
  try {
    // Backend ko request bhejein
    const res = await axios.put(`${API_BASE_URL}/orders/update-status/${id}`, {
      status: newStatus
    });

    // Check karein ki backend ne 'success: true' bheja hai ya nahi
    if (res.data.success) {
      // Local state update karein taaki UI bina refresh ke badal jaye
      setOrders(prev => prev.map(o => o._id === id ? { ...o, status: newStatus } : o));
      
      Swal.fire({
        icon: "success",
        title: "Status Updated",
        text: `Order marked as ${newStatus}`,
        timer: 1000,
        showConfirmButton: false
      });
    } else {
      // Agar status 200 hai par success false hai (logic error)
      Swal.fire("Warning", res.data.message || "Failed to update", "warning");
    }
  } catch (err) {
    console.error("Update Error:", err);
    // Agar API crash ho jaye ya network issue ho
    Swal.fire("Error", "Backend se connect nahi ho paya. Server restart karke dekhein.", "error");
  }
};

  // 🗑️ DELETE ORDER
  const deleteOrder = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Order will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/orders/delete/${id}`);
        if (response.status >= 200 && response.status < 300) {
          setOrders((prevOrders) => prevOrders.filter((order) => order._id !== id));
          Swal.fire({ icon: "success", title: "Deleted!", timer: 1500, showConfirmButton: false });
        }
      } catch (error) {
        Swal.fire({ icon: "error", title: "Delete Failed", text: error.response?.data?.message || "Server error" });
      }
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesName = order.parentName?.toLowerCase().includes(searchName.toLowerCase());
    const matchesSchool = searchSchool === "" || order.schoolName === searchSchool;

    let matchesStatus = false;
    if (viewMode === "all") matchesStatus = true;
    if (viewMode === "active") matchesStatus = ACTIVE_STATUS.includes(order.status);
    if (viewMode === "shipped") matchesStatus = SHIPPED_STATUS.includes(order.status);
    if (viewMode === "cancelled") matchesStatus = CANCEL_STATUS.includes(order.status);

    return matchesName && matchesSchool && matchesStatus;
  });

  const activeCount = orders.filter(o => ACTIVE_STATUS.includes(o.status)).length;
  const shippedCount = orders.filter(o => SHIPPED_STATUS.includes(o.status)).length;
  const cancelledCount = orders.filter(o => CANCEL_STATUS.includes(o.status)).length;

  if (loading) return <div className="p-5 text-center">Loading Orders...</div>;

  return (
    <div className="container-fluid py-4" style={{ background: "#f5f7fb", minHeight: "100vh" }}>
      
      <button className="btn btn-md d-flex align-items-center gap-2 fw-bold text-muted border-0 bg-transparent mb-3" onClick={() => navigate("/dashboard")}>
        <FaArrowLeft /> Back
      </button>

      <div className="mb-4 p-4 bg-white shadow-sm rounded-4 border-start border-success border-5 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h3 className="fw-bold text-dark mb-0">
            {viewMode === "shipped" ? "Shipped Records" : viewMode === "cancelled" ? "Cancelled Records" : "Order Management"}
          </h3>
          <p className="text-muted small mb-0">Change order status and track progress</p>
        </div>

        <div className="d-flex p-1 bg-light rounded-3 shadow-sm">
          <button className={`btn px-4 py-2 fw-bold border-0 ${viewMode === "all" ? "btn-success shadow-sm" : "text-muted"}`} onClick={() => setViewMode("all")}>
            All ({orders.length})
          </button>
          <button className={`btn px-4 py-2 fw-bold border-0 ${viewMode === "active" ? "btn-success shadow-sm" : "text-muted"}`} onClick={() => setViewMode("active")}>
            Active ({activeCount})
          </button>
          <button className={`btn px-4 py-2 fw-bold border-0 ${viewMode === "shipped" ? "btn-info text-white shadow-sm" : "text-muted"}`} onClick={() => setViewMode("shipped")}>
            Shipped ({shippedCount})
          </button>
          <button className={`btn px-4 py-2 fw-bold border-0 ${viewMode === "cancelled" ? "btn-danger text-white shadow-sm" : "text-muted"}`} onClick={() => setViewMode("cancelled")}>
            Cancelled ({cancelledCount})
          </button>
        </div>
      </div>

      {/* SEARCH AND FILTER */}
      <div className="d-flex gap-2 mb-3">
        <div className="position-relative" style={{ width: "250px" }}>
          <FaSearch className="position-absolute top-50 translate-middle-y ms-3 text-muted" />
          <input type="text" className="form-control ps-5 border-0 shadow-sm" style={{ borderRadius: "10px", height: "45px" }} placeholder="Search Parent..." value={searchName} onChange={(e) => setSearchName(e.target.value)} />
        </div>
        <div className="position-relative" style={{ width: "220px" }}>
          <FaFilter className="position-absolute top-50 translate-middle-y ms-3 text-muted" />
          <select className="form-control ps-5 border-0 shadow-sm" style={{ borderRadius: "10px", height: "45px" }} value={searchSchool} onChange={(e) => setSearchSchool(e.target.value)}>
            <option value="">All Schools</option>
            {allSchools.map((s) => <option key={s._id} value={s.name}>{s.name}</option>)}
          </select>
        </div>
      </div>

      <div className="table-responsive shadow-sm" style={{ borderRadius: "12px", border: "1px solid #e0e0e0", backgroundColor: "white" }}>
        <table className="table table-hover mb-0">
          <thead>
            <tr style={{ color: "#444", fontSize: "16px" }}>
              <th className="py-3 px-4 border-0" style={{ backgroundColor: "#edf9f5" }}>ID</th>
              <th className="py-3 border-0" style={{ backgroundColor: "#edf9f5" }}>School</th>
              <th className="py-3 border-0" style={{ backgroundColor: "#edf9f5" }}>Parent Details</th>
              <th className="py-3 border-0 text-center" style={{ backgroundColor: "#edf9f5" }}>Amount</th>
              <th className="py-3 border-0 text-center" style={{ backgroundColor: "#edf9f5" }}>Status Update</th>
              <th className="py-3 border-0 text-center px-4" style={{ backgroundColor: "#edf9f5" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order) => (
                <tr key={order._id} className="align-middle">
                  <td className="py-3 px-4 text-muted">#{order._id.slice(-6).toUpperCase()}</td>
                  <td className="py-3 text-muted">{order.schoolName}</td>
                  <td className="py-3">
                    <div className=" py-3 text-muted ">{order.parentName}</div>
                    <small className="text-muted">{order.phone}</small>
                  </td>
                  <td className="py-3 text-center text-success fw-bold">₹{order.totalAmount}</td>
                  
                  {/* STATUS DROPDOWN */}
                  <td className="py-3 text-center">
                    <select 
                      className={`form-select form-select-sm fw-bold border-0 shadow-sm mx-auto ${order.status === 'Shipped' ? 'bg-info text-white' : 'bg-light'}`}
                      style={{ width: "150px", borderRadius: "8px" }}
                      value={order.status || "Pending"}
                      onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                    >
                      {STATUS_OPTIONS.map(opt => (
                        <option key={opt} value={opt} className="bg-white text-dark">{opt}</option>
                      ))}
                    </select>
                  </td>

                  <td className="py-3 text-center px-4">
                    <div className="d-flex justify-content-center gap-2">
                      <Link to={`/dashboard/order-details/${order._id}`} className="btn btn-sm btn-light border shadow-sm"><FaEye /></Link>
                      <button onClick={() => deleteOrder(order._id)} className="btn btn-sm btn-outline-danger border shadow-sm"><FaTrash /></button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="6" className="text-center py-5 text-muted">No orders found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Orders;
